/**
 * Whether the page's rendered authorization verdict can stand in for the first
 * `api/accounts/IsAuthorized` POST (issue #1034).
 *
 * Pure and angular-free so it can be tested directly; the service that owns
 * the cache does the JWT parsing and calls this with the two facts that decide
 * it.
 */

export interface RenderedAuth {
    userId: string;
    email: string;
    authorized: boolean;
}

/**
 * The seed says what the SERVER concluded about the `latestgameplantoken`
 * cookie while rendering. The client's own token lives in localStorage under
 * `authorizationData`, and the two are written at different times — on login
 * and after each successful check — so they can name different users or the
 * cookie can be absent entirely.
 *
 * Every rejection here falls back to today's behaviour: the caller does the
 * POST. So this answers "may we skip the ask?", never "is this user allowed?".
 *
 * @param clientTokenUserId `nameid` from the token in localStorage, or "" when
 *   there is no usable token.
 * @param clientTokenExpirySeconds the token's `exp`, or undefined when it
 *   carries none — matching AuthorisationCheckerService, which treats a token
 *   without `exp` as unexpired rather than rejecting it.
 */
export function gpRenderedAuthSeedApplies(
    rendered: RenderedAuth | undefined,
    clientTokenUserId: string,
    clientTokenExpirySeconds: number | undefined,
    nowSeconds: number,
): boolean {
    if (!rendered || !rendered.authorized || !rendered.userId) {
        return false;
    }

    if (!clientTokenUserId || clientTokenUserId !== rendered.userId) {
        return false;
    }

    if (clientTokenExpirySeconds && clientTokenExpirySeconds <= nowSeconds) {
        return false;
    }

    return true;
}
